import React from 'react';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import { APP_URLS } from 'constants/urls';
import { NavItem, NavItemTypeEnum } from '@lib/components/Menu/Menu';
import Icons from '@lib/components/Icons/Icons';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import { NavItemArgs } from './types';

const lockChipRightIcon = (isCollapsed?: boolean) => (
  <Chip
    leftIcon="lock"
    size={isCollapsed ? ChipSizes.extraSmall : ChipSizes.small}
    type={ChipTypes.chipBlue}
  />
);

export default function getDocumentationNavItem(args: NavItemArgs) {
  const {
    pathname,
    t,
    getUserAccessByComponent,
    getUserAccessByCompany,
    isFullAdmin,
    myDocumentationApprovalsCounter,
  } = args;

  const isDocumentsAvailable =
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) !==
    AccessLevelEnum.LimitedRead;
  const isDocumentsLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  // Lock-chip affordances on premium nav children are only meaningful for
  // Full Admins (they can open the plan-upgrade modal); for the User role,
  // skip the locked children entirely.
  const showLockedNavChildren = isDocumentsLocked && isFullAdmin;
  const isAnalyticsAvailable =
    getUserAccessByComponent(AccessComponentEnum.DocumentationAnalytics) !==
    AccessLevelEnum.LimitedRead;
  const isCategoriesAvailable =
    getUserAccessByComponent(
      AccessComponentEnum.DocumentationCategoriesManagement,
    ) !== AccessLevelEnum.LimitedRead;

  const documentationBasePath = APP_URLS.dashboard.documentation.index.path;
  const otherSubNavPaths = [
    APP_URLS.dashboard.documentation.analytics.path,
    APP_URLS.dashboard.documentation.approvals.path,
    APP_URLS.dashboard.documentation.categories.path,
  ];

  const isDocumentsSubMenuShown = isDocumentsAvailable || showLockedNavChildren;
  let to = APP_URLS.dashboard.documentation.index.getPathWithQuery();

  const children: NavItem<unknown>[] = [];
  if (isDocumentsSubMenuShown) {
    children.push({
      id: 'documents',
      title: t('documents'),
      to: APP_URLS.dashboard.documentation.index.getPathWithQuery(),
      type: NavItemTypeEnum.PrimarySub,
      end: true,
      predictorFnActive: () =>
        pathname.startsWith(documentationBasePath) &&
        !otherSubNavPaths.some((p) => pathname.startsWith(p)),
    });
    children.push({
      id: 'documentation-approvals',
      title: t('approvals'),
      to: APP_URLS.dashboard.documentation.approvals.getPathWithQuery(),
      type: NavItemTypeEnum.PrimarySub,
      end: true,
      rightIcon: isDocumentsLocked ? lockChipRightIcon : undefined,
      predictorFnVisible: () => !!myDocumentationApprovalsCounter,
    });
  }
  // When EDM is plan-locked, Analytics/Categories become locked CTAs only
  // a Full Admin can act on — so for User-role accounts hide them entirely,
  // even if their role grants component-level access.
  const showAnalyticsChild = isDocumentsLocked
    ? showLockedNavChildren
    : isAnalyticsAvailable;
  const showCategoriesChild = isDocumentsLocked
    ? showLockedNavChildren
    : isCategoriesAvailable;
  if (showAnalyticsChild) {
    children.push({
      id: 'documentation-analytics',
      title: t('analytics'),
      to: APP_URLS.dashboard.documentation.analytics.getPathWithQuery(),
      type: NavItemTypeEnum.PrimarySub,
      end: true,
      rightIcon: isDocumentsLocked ? lockChipRightIcon : undefined,
    });
    if (!isDocumentsSubMenuShown) {
      to = APP_URLS.dashboard.documentation.analytics.getPathWithQuery();
    }
  }
  if (showCategoriesChild) {
    children.push({
      id: 'documentation-categories',
      title: t('categories'),
      to: APP_URLS.dashboard.documentation.categories.getPathWithQuery(),
      type: NavItemTypeEnum.PrimarySub,
      end: true,
      rightIcon: isDocumentsLocked ? lockChipRightIcon : undefined,
    });
    if (!isDocumentsSubMenuShown && !isAnalyticsAvailable) {
      to = APP_URLS.dashboard.documentation.categories.getPathWithQuery();
    }
  }

  return {
    id: 'documentation',
    title: t('documentation'),
    leftIcon: Icons.description,
    to,
    type: NavItemTypeEnum.Primary,
    predictorFnVisible: () =>
      isDocumentsAvailable ||
      showLockedNavChildren ||
      isAnalyticsAvailable ||
      isCategoriesAvailable,
    predictorFnActive: () => pathname.startsWith(documentationBasePath),
    children,
  };
}
